import { Tooltip } from '@mui/material';
import type { ImageProps } from 'next/image';

type Threshold = {
  imageUrl: string;
  minValue: number;
  maxValue: number;
};

export type DynamicBadgeProps = {
  height?: number;
  width?: number;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  translatedText: {
    tooltipText: string;
  };
  thresholds: Threshold[];
  value: number;
  testId?: string;
};

/**
 * Props for the DynamicBadge component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @param {DynamicBadgeProps} props - The props for the DynamicBadge component.
 * @param {number} height - The height of the badge.
 * @param {number} width - The width of the badge.
 * @param {Threshold[]} thresholds - The thresholds for the badge.
 * @param {number} platformScoreToCheckAgainst - The score to check against the thresholds.
 * @param {React.ComponentType<ImageProps>} FlexibleImageComponent - The image component to use.
 * @param {string} translatedText.tooltipText - The text to display in the tooltip.
 */

export const DynamicBadge = ({
  height,
  width,
  FlexibleImageComponent,
  translatedText,
  thresholds,
  value,
  testId = 'dynamic-badge',
}: DynamicBadgeProps) => {
  const roundedScore = Math?.round(value) ?? 0;

  if (roundedScore === 0) {
    return null;
  }

  const badgeSrc: string | undefined = thresholds?.find(
    (data: Threshold) =>
      roundedScore >= data.minValue && roundedScore <= data.maxValue
  )?.imageUrl;

  if (!badgeSrc) {
    return null;
  }

  return (
    <Tooltip title={translatedText?.tooltipText ?? ''}>
      <FlexibleImageComponent
        src={badgeSrc}
        alt={translatedText?.tooltipText ?? ''}
        height={height ?? 100}
        width={width ?? 100}
        data-testid={testId}
      />
    </Tooltip>
  );
};
